import React, { useMemo } from 'react';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { GROWTH_SCHEMA_META } from '../../growth-schema/config';
import {
  SC_FAQS,
  SC_INSTALL,
  SC_PRIORITY,
  SC_SCHEMAS,
  SC_SITELINKS,
  SC_STATUS,
  SC_VALIDATION,
} from '../../growth-schema/mockData';
import type { ScInstallInfo, ScMetricTile, ScSchemaItem, ScToneKey, ScValidation } from '../../growth-schema/types';
import './growth-schema.css';

const SC_TONE: Record<ScToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const scEyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

function ScCodeIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--ai-3)]"
    >
      <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
    </svg>
  );
}

function ScCard({
  children,
  className = '',
  padding = true,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        padding ? 'p-[22px]' : '',
        className,
      ].join(' ')}
      style={style}
    >
      {children}
    </div>
  );
}

function ScInstallBanner({ install }: { install: ScInstallInfo }) {
  const installed = install.status === 'installed';
  const tone = installed ? SC_TONE.success : SC_TONE.gold;

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-[18px] overflow-hidden rounded-[var(--r-xl)] border p-5 shadow-[var(--sh-1)]"
      style={{
        borderColor: `color-mix(in srgb, ${tone.accent} 30%, transparent)`,
        background: `linear-gradient(135deg, ${tone.tint}, var(--cream-1) 60%)`,
      }}
    >
      <span className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[13px] bg-[var(--cream-1)] text-[var(--success)] shadow-[var(--sh-1)]">
        <Icon kind={installed ? 'check-circ' : 'alert'} size={23} stroke={1.9} style={{ color: tone.accent }} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-[5px] flex flex-wrap items-center gap-2.5 leading-[1.2]">
          <span className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {installed ? 'Schema Injector is active' : 'Schema Injector needs installation'}
          </span>
          {installed ? (
            <span className="inline-flex items-center gap-[5px] rounded-[var(--r-pill)] bg-[var(--success-tint)] px-[9px] py-[3px] text-[11px] font-bold text-[var(--success)]">
              <span className="elva-live h-[7px] w-[7px]" />
              Live
            </span>
          ) : null}
        </div>
        <div className="text-[13px] leading-[1.55] text-[var(--ink-2)]">
          {installed ? (
            <>
              ELVA&apos;s script is installed and detecting{' '}
              <b className="font-semibold">{install.pagesDetected} pages</b>. Schema deploys to your
              site automatically — status reflects injector pings, not live Rich Results verification.
            </>
          ) : (
            <>
              Install ELVA&apos;s one-time script to enable automatic schema deployment. Takes your
              webmaster about 5 minutes.
            </>
          )}
        </div>
        {installed && install.installedDate && install.lastPing ? (
          <div className="mt-2 flex flex-wrap gap-3 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
            <span>Installed {install.installedDate}</span>
            <span className="text-[var(--cream-shadow)]">·</span>
            <span>Last ping {install.lastPing}</span>
            <span className="text-[var(--cream-shadow)]">·</span>
            <span>{install.pagesDetected} pages</span>
          </div>
        ) : null}
      </div>
      <Button size="sm" variant={installed ? 'secondary' : 'primary'}>
        {installed ? 'View install details' : 'Get install instructions'}
      </Button>
    </div>
  );
}

function ScMetric({
  label,
  value,
  total,
  sub,
  tone,
}: {
  label: string;
  value: string;
  total?: string;
  sub: string;
  tone?: ScToneKey;
}) {
  const t = tone ? (SC_TONE[tone] ?? SC_TONE.neutral) : null;
  return (
    <ScCard className="px-[18px] py-4">
      <div className={scEyebrowClass}>{label}</div>
      <div className="mt-2 flex items-baseline gap-[5px]">
        <span
          className="font-[var(--font-display)] text-2xl font-extrabold leading-none tracking-[-0.04em]"
          style={{ color: t ? t.accent : 'var(--ink-0)' }}
        >
          {value}
        </span>
        {total ? <span className="text-[13px] font-semibold text-[var(--ink-4)]">{total}</span> : null}
      </div>
      <div className="mt-1.5 text-[11.5px] text-[var(--ink-3)]">{sub}</div>
    </ScCard>
  );
}

function ScSnippetPreview() {
  const meta = GROWTH_SCHEMA_META;
  return (
    <ScCard>
      <div className="mb-4">
        <div className="font-[var(--font-display)] text-[16.5px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          How you appear in Google
        </div>
        <div className="mt-[3px] text-[12.5px] text-[var(--ink-3)]">Your schema powers these rich results</div>
      </div>
      <div className="mb-3.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-[18px] py-4">
        <div className="mb-1 font-[var(--font-mono)] text-xs text-[var(--ink-3)]">{meta.snippetUrl}</div>
        <div className="mb-[5px] text-lg font-medium tracking-[-0.01em] text-[var(--info)]">
          {meta.snippetTitle}
        </div>
        <div className="mb-[7px] flex flex-wrap items-center gap-[7px]">
          <span className="inline-flex gap-px">
            {[0, 1, 2, 3, 4].map((i) => (
              <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill="var(--gold)">
                <path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.6 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" />
              </svg>
            ))}
          </span>
          <span className="text-[12.5px] text-[var(--ink-2)]">
            <b className="font-bold">{meta.snippetRating}</b> ({meta.snippetReviews})
          </span>
          <span className="text-[12.5px] text-[var(--ink-3)]">· {meta.snippetLocation}</span>
        </div>
        <div className="mb-[11px] text-[13px] leading-[1.5] text-[var(--ink-2)]">
          {meta.snippetDescription}
        </div>
        <div className="mb-2.5 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-[13px] py-2">
          {SC_FAQS.map((question, index) => (
            <div
              key={question}
              className="flex items-center gap-[7px] py-1.5 text-[12.5px] text-[var(--ink-2)]"
              style={{
                borderBottom: index < SC_FAQS.length - 1 ? '1px solid var(--cream-2)' : undefined,
              }}
            >
              <Icon kind="down" size={11} stroke={2.4} className="shrink-0 text-[var(--ink-4)]" />
              {question}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-[18px] gap-y-1.5">
          {SC_SITELINKS.map((link) => (
            <span key={link} className="text-[12.5px] text-[var(--info)]">
              {link}
            </span>
          ))}
        </div>
      </div>
      <div className="rounded-[var(--r-md)] bg-[var(--ai-tint)] px-3.5 py-3 text-xs leading-[1.6] text-[var(--ink-2)]">
        <b className="font-bold text-[var(--ai-3)]">What powers this:</b>{' '}
        {meta.snippetPowersNote.replace('What powers this: ', '')}
      </div>
    </ScCard>
  );
}

function ScValidationPanel({
  validation,
  controlled = false,
}: {
  validation: ScValidation;
  controlled?: boolean;
}) {
  const clean = validation.errors === 0 && validation.warnings === 0;
  const toneKey: ScToneKey =
    validation.errors > 0 ? 'terra' : validation.warnings > 0 ? 'gold' : 'success';
  const tone = SC_TONE[toneKey];

  const statusTitle = clean
    ? validation.valid > 0
      ? `${validation.valid} schema type${validation.valid === 1 ? '' : 's'} in valid bucket`
      : 'No validation issues in ELVA buckets'
    : validation.errors > 0
      ? `${validation.errors} error${validation.errors !== 1 ? 's' : ''} in buckets`
      : `${validation.warnings} bucket warning${validation.warnings !== 1 ? 's' : ''}`;

  return (
    <ScCard>
      <div className="mb-4">
        <div className="font-[var(--font-display)] text-[16.5px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Validation status
        </div>
        <div className="mt-[3px] text-[12.5px] text-[var(--ink-3)]">
          {controlled
            ? 'Bucket counts from ELVA — not live schema.org / Rich Results validation'
            : "Tested against Google's Rich Results validator"}
        </div>
      </div>
      <div
        className="mb-3.5 flex items-center gap-3.5 rounded-[var(--r-md)] border px-[18px] py-4"
        style={{
          background: tone.tint,
          borderColor: `color-mix(in srgb, ${tone.accent} 26%, transparent)`,
        }}
      >
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[var(--cream-1)]">
          <Icon
            kind={clean ? 'check' : validation.errors > 0 ? 'x' : 'alert'}
            size={20}
            stroke={2.4}
            style={{ color: tone.accent }}
          />
        </span>
        <div className="flex-1">
          <div className="mb-0.5 text-[13.5px] font-semibold" style={{ color: tone.accent }}>
            {statusTitle}
          </div>
          <div className="text-xs text-[var(--ink-3)]">Last validated {validation.lastValidated}</div>
        </div>
      </div>
      {validation.issues.length > 0 ? (
        <div className="flex flex-col gap-2">
          {validation.issues.map((issue) => {
            const issueTone = SC_TONE[issue.severity === 'warning' ? 'gold' : 'terra'];
            return (
              <div
                key={`${issue.schema}-${issue.message}`}
                className="flex items-start gap-2 rounded-[var(--r-md)] border px-3.5 py-2.5 text-[12.5px] text-[var(--ink-1)]"
                style={{
                  background: issueTone.tint,
                  borderColor: `color-mix(in srgb, ${issueTone.accent} 26%, transparent)`,
                }}
              >
                <Icon
                  kind="alert"
                  size={14}
                  stroke={2}
                  className="mt-px shrink-0"
                  style={{ color: issueTone.accent }}
                />
                <span>
                  <b className="font-bold" style={{ color: issueTone.accent }}>
                    {issue.schema}:
                  </b>{' '}
                  {issue.message}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
      <button
        type="button"
        className="sc-link mt-3.5 inline-flex cursor-pointer items-center gap-[7px] rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2 font-[var(--font-body)] text-[12.5px] font-semibold text-[var(--ink-1)]"
      >
        <Icon kind="external" size={13} stroke={1.9} />
        View in Google&apos;s Rich Results Test
      </button>
    </ScCard>
  );
}

function ScSchemaCard({
  schema,
  controlled = false,
  onHandoff,
  onMarkDeployed,
  handoffPageId,
  markingPageId,
}: {
  schema: ScSchemaItem;
  controlled?: boolean;
  onHandoff?: (pageId: number) => void;
  onMarkDeployed?: (pageId: number) => void;
  handoffPageId?: number;
  markingPageId?: number;
}) {
  const status = SC_STATUS[schema.status] ?? SC_STATUS.missing;
  const statusTone = SC_TONE[status.tone] ?? SC_TONE.neutral;
  const priority = SC_PRIORITY[schema.priority] ?? SC_PRIORITY.recommended;
  const priorityTone = SC_TONE[priority.tone] ?? SC_TONE.neutral;
  const isActive = schema.status === 'active' || schema.status === 'warning';
  const statusLabel =
    controlled && schema.status === 'active' ? 'Marked deployed' : status.label;
  const canHandoff = schema.pageId != null;
  const canMarkDeployed = schema.pageId != null;
  const handoffPending = canHandoff && handoffPageId === schema.pageId;
  const markPending = canMarkDeployed && markingPageId === schema.pageId;

  return (
    <ScCard
      className="px-5 py-[18px]"
      style={{ opacity: schema.status === 'missing' ? 0.78 : 1 }}
    >
      <div className="flex items-start gap-3.5">
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
          style={{ background: statusTone.tint }}
        >
          <span className="h-[9px] w-[9px] rounded-full" style={{ background: statusTone.accent }} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-[5px] flex flex-wrap items-center gap-2">
            <span className="font-[var(--font-display)] text-[15.5px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
              {schema.name}
            </span>
            <span
              className="rounded-[var(--r-pill)] px-2 py-0.5 text-[10px] font-bold"
              style={{ background: statusTone.tint, color: statusTone.accent }}
            >
              {statusLabel}
            </span>
            <span
              className="rounded-[var(--r-pill)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em]"
              style={{ background: priorityTone.tint, color: priorityTone.accent }}
            >
              {priority.label}
            </span>
          </div>
          <p className="m-0 mb-3 text-[12.5px] leading-[1.55] text-[var(--ink-2)]">
            {schema.description}
          </p>
          {isActive ? (
            <div className="sc-schema-meta grid grid-cols-3 gap-3 border-t border-[var(--cream-2)] pt-[11px]">
              {[
                ['Source', schema.source],
                ['Deployed on', schema.deployedOn],
                ['Last updated', schema.lastUpdated ?? '—'],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className={`${scEyebrowClass} mb-[3px]`}>{label}</div>
                  <div className="text-xs font-medium text-[var(--ink-1)]">{value}</div>
                </div>
              ))}
            </div>
          ) : null}
          {controlled && isActive ? (
            <p className="mt-2 text-[11px] italic text-[var(--ink-3)]">
              Marked deployed by you — not verified live in Google Rich Results.
            </p>
          ) : null}
          {schema.issue ? (
            <div className="mt-2.5 flex items-start gap-[7px] rounded-[var(--r-sm)] bg-[var(--gold-tint)] px-3 py-2 text-xs text-[var(--ink-1)]">
              <Icon kind="alert" size={13} stroke={2} className="mt-px shrink-0 text-[var(--gold)]" />
              <span className="leading-[1.5]">{schema.issue}</span>
            </div>
          ) : null}
          {schema.status === 'missing' && onHandoff ? (
            canHandoff ? (
              <button
                type="button"
                onClick={() => onHandoff(schema.pageId!)}
                disabled={handoffPending}
                className="sc-link mt-2.5 cursor-pointer rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--ai-3)_40%,transparent)] bg-[var(--ai-tint)] px-3.5 py-[7px] font-[var(--font-body)] text-[12.5px] font-semibold text-[var(--ai-3)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {handoffPending ? 'Queueing deploy…' : 'Deploy this schema'}
              </button>
            ) : (
              <p className="mt-2.5 text-[11.5px] leading-[1.5] text-[var(--ink-3)]">
                Deploy unavailable — no numeric page id in page_ids for this schema type.
              </p>
            )
          ) : schema.status === 'missing' ? (
            <button
              type="button"
              className="sc-link mt-2.5 cursor-pointer rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--ai-3)_40%,transparent)] bg-[var(--ai-tint)] px-3.5 py-[7px] font-[var(--font-body)] text-[12.5px] font-semibold text-[var(--ai-3)]"
            >
              Deploy this schema
            </button>
          ) : null}
          {schema.status === 'warning' && onMarkDeployed ? (
            canMarkDeployed ? (
              <button
                type="button"
                onClick={() => onMarkDeployed(schema.pageId!)}
                disabled={markPending}
                className="sc-link mt-2.5 cursor-pointer rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-[7px] font-[var(--font-body)] text-[13px] font-semibold text-[var(--ink-1)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {markPending ? 'Saving…' : 'Mark deployed (self-attested)'}
              </button>
            ) : (
              <p className="mt-2.5 text-[11.5px] leading-[1.5] text-[var(--ink-3)]">
                Mark deployed unavailable — numeric page id missing from API response.
              </p>
            )
          ) : null}
        </div>
      </div>
    </ScCard>
  );
}

export interface SchemaManagerViewProps {
  items?: ScSchemaItem[];
  install?: ScInstallInfo;
  validation?: ScValidation;
  metrics?: ScMetricTile[];
  loading?: boolean;
  onHandoff?: (pageId: number) => void;
  onMarkDeployed?: (pageId: number) => void;
  handoffPageId?: number;
  markingPageId?: number;
}

export function SchemaManagerView({
  items: itemsProp,
  install: installProp,
  validation: validationProp,
  metrics: metricsProp,
  loading = false,
  onHandoff,
  onMarkDeployed,
  handoffPageId,
  markingPageId,
}: SchemaManagerViewProps = {}) {
  const meta = GROWTH_SCHEMA_META;
  const controlled = itemsProp !== undefined;
  const schemas = controlled ? itemsProp : SC_SCHEMAS;
  const install = installProp ?? SC_INSTALL;
  const validation = validationProp ?? SC_VALIDATION;

  const essentialTotal = useMemo(
    () => schemas.filter((s) => s.priority === 'essential').length,
    [schemas],
  );
  const essentialDone = useMemo(
    () => schemas.filter((s) => s.priority === 'essential' && s.status === 'active').length,
    [schemas],
  );
  const validTone: ScToneKey =
    validation.errors > 0 ? 'terra' : validation.warnings > 0 ? 'gold' : 'success';

  const validationValue =
    validation.errors === 0 && validation.warnings === 0
      ? 'Clean'
      : validation.errors > 0
        ? `${validation.errors} errors`
        : `${validation.warnings} warning`;

  const metrics = metricsProp ?? [
    {
      key: 'types_active',
      label: 'Schema types active',
      value: String(validation.valid),
      total: `/ ${schemas.length}`,
      sub: 'across all pages',
      tone: 'ai' as const,
    },
    {
      key: 'essential_coverage',
      label: 'Essential coverage',
      value: `${essentialDone} / ${essentialTotal}`,
      sub: 'essential schemas marked deployed',
      tone: (essentialDone === essentialTotal ? 'success' : 'gold') as ScToneKey,
    },
    {
      key: 'pages_covered',
      label: 'Pages covered',
      value: String(install.pagesDetected),
      sub: 'via injector script',
      tone: 'info' as const,
    },
    {
      key: 'validation',
      label: 'Validation buckets',
      value: validationValue,
      sub: 'ELVA bucket counts',
      tone: validTone,
    },
  ];

  return (
    <div className="growth-schema-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <ScCodeIcon />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      <ScInstallBanner install={install} />

      <div
        className="sc-metrics-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {metrics.map((metric) => (
          <ScMetric
            key={metric.key}
            label={metric.label}
            value={metric.value}
            {...(metric.total ? { total: metric.total } : {})}
            sub={metric.sub ?? ''}
            {...(metric.tone ? { tone: metric.tone } : {})}
          />
        ))}
      </div>

      <div
        className="sc-preview-grid mb-[30px] grid gap-4"
        style={{ gridTemplateColumns: '1.2fr 1fr' }}
      >
        <ScSnippetPreview />
        <ScValidationPanel validation={validation} controlled={controlled} />
      </div>

      <div className="mb-4">
        <h2 className="m-0 mb-[5px] font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Deployed schema types
        </h2>
        <p className="m-0 text-[13px] text-[var(--ink-3)]">
          Each type has a specific purpose. ELVA pulls data from the listed source, keeps it synced,
          and updates automatically when anything changes.
        </p>
      </div>

      <div className="mb-7 flex flex-col gap-3">
        {schemas.map((schema) => (
          <ScSchemaCard
            key={schema.id}
            schema={schema}
            controlled={controlled}
            {...(onHandoff ? { onHandoff } : {})}
            {...(onMarkDeployed ? { onMarkDeployed } : {})}
            {...(handoffPageId != null ? { handoffPageId } : {})}
            {...(markingPageId != null ? { markingPageId } : {})}
          />
        ))}
      </div>

      <div className="flex items-start gap-3.5 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--ai-3)_22%,transparent)] bg-[var(--ai-tint)] p-[18px_22px]">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--cream-1)] text-[var(--ai-3)]">
          <Icon kind="sparkle" size={17} stroke={1.9} />
        </span>
        <div className="flex-1 text-[13px] leading-[1.65] text-[var(--ink-2)]">
          <b className="text-[var(--ink-0)]">How ELVA schema injection works:</b> Your webmaster
          installs a single{' '}
          <code className="rounded bg-[var(--cream-1)] px-1.5 py-px font-[var(--font-mono)] text-xs text-[var(--ink-1)]">
            &lt;script&gt;
          </code>{' '}
          tag once. That script calls ELVA&apos;s API on each page load and injects the correct schema
          for that page. Update your hours in the PMS and we update the schema; a new GBP review lands
          and we update the rating — no developer needed for ongoing changes.
        </div>
      </div>
    </div>
  );
}
